import React, { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { useApp } from "@/context/AppContext";
import { Sun, Menu, X, LogOut, Globe } from "lucide-react";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
} from "@/components/ui/dropdown-menu";
import {
  Sheet, SheetContent, SheetTrigger, SheetTitle, SheetDescription,
} from "@/components/ui/sheet";

export const Header = () => {
  const { user, logout, lang, setLang, t } = useApp();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);

  const linkCls = ({ isActive }) =>
    `text-sm font-medium transition-colors ${isActive ? "text-[#17301C]" : "text-[#3A4A3E] hover:text-[#17301C]"}`;

  const mobileLinkCls = ({ isActive }) =>
    `flex items-center justify-between px-4 py-3.5 rounded-xl text-base font-semibold border border-[#DDE0D8] transition-colors ${isActive ? "bg-[#EAB308] text-[#17301C]" : "bg-white text-[#17301C] hover:bg-[#EAE7E0]"}`;

  const closeAnd = () => setOpen(false);

  const navItems = [
    { to: "/calculator", label: t.nav.calculator, testid: "nav-calculator" },
    ...(user ? [{ to: "/dashboard", label: t.nav.dashboard, testid: "nav-dashboard" }] : []),
    { to: "/weather", label: "Meteo", testid: "nav-weather" },
    { to: "/coach", label: "Coach", testid: "nav-coach" },
    { to: "/community", label: "Community", testid: "nav-community" },
    ...(user ? [{ to: "/referral", label: "Invita amici", testid: "nav-referral" }] : []),
    { to: "/marketplace", label: t.nav.marketplace, testid: "nav-marketplace" },
    { to: "/grants", label: t.nav.grants, testid: "nav-grants" },
    { to: "/investment", label: "Investimento", testid: "nav-investment" },
    { to: "/pricing", label: "Pro €3,50", testid: "nav-pricing" },
  ];

  return (
    <header className="sticky top-0 z-50 backdrop-blur-xl bg-white/70 border-b border-white/40 shadow-sm">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between gap-3">
        <Link to="/" className="flex items-center gap-2 min-w-0" data-testid="logo-link">
          <img src="/logo.jpg" alt="SolarControl Minniti" className="h-10 w-10 sm:h-11 sm:w-11 object-contain rounded-full bg-white flex-shrink-0" />
          <div className="flex flex-col leading-none min-w-0">
            <span className="text-sm sm:text-base font-black tracking-tight truncate">
              <span className="text-[#EAB308]">Solar</span><span className="text-[#17301C]">Control</span>
            </span>
            <span className="text-[9px] sm:text-[10px] font-black tracking-[0.35em] text-[#17301C] uppercase">Minniti</span>
          </div>
        </Link>

        {/* Desktop nav */}
        <nav className="hidden lg:flex items-center gap-5">
          <NavLink to="/calculator" className={linkCls} data-testid="nav-calculator">{t.nav.calculator}</NavLink>
          {user && <NavLink to="/dashboard" className={linkCls} data-testid="nav-dashboard">{t.nav.dashboard}</NavLink>}
          <NavLink to="/weather" className={linkCls} data-testid="nav-weather">Meteo</NavLink>
          <NavLink to="/coach" className={linkCls} data-testid="nav-coach">Coach</NavLink>
          <NavLink to="/community" className={linkCls} data-testid="nav-community">Community</NavLink>
          {user && <NavLink to="/referral" className={linkCls} data-testid="nav-referral">Invita</NavLink>}
          <NavLink to="/marketplace" className={linkCls} data-testid="nav-marketplace">{t.nav.marketplace}</NavLink>
          <NavLink to="/grants" className={linkCls} data-testid="nav-grants">{t.nav.grants}</NavLink>
          <NavLink to="/investment" className={linkCls} data-testid="nav-investment">Investimento</NavLink>
          <NavLink to="/pricing" className={linkCls} data-testid="nav-pricing">Pro</NavLink>
        </nav>

        <div className="flex items-center gap-2 sm:gap-3">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button data-testid="lang-toggle" className="flex items-center gap-1 px-2.5 sm:px-3 py-2 rounded-full border border-[#DDE0D8] text-xs font-bold uppercase tracking-widest text-[#17301C] transition-colors hover:bg-[#EAE7E0]">
                <Globe size={14} /> {lang.toUpperCase()}
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="bg-white">
              <DropdownMenuItem onClick={() => setLang("it")} data-testid="lang-it">Italiano 🇮🇹</DropdownMenuItem>
              <DropdownMenuItem onClick={() => setLang("en")} data-testid="lang-en">English 🇬🇧</DropdownMenuItem>
              <DropdownMenuItem onClick={() => setLang("pl")} data-testid="lang-pl">Polski 🇵🇱</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          {user ? (
            <div className="flex items-center gap-2">
              {user.is_pro && (
                <span className="hidden md:inline-flex items-center gap-1 text-[10px] font-black tracking-widest uppercase px-2 py-1 rounded-full bg-[#EAB308] text-[#17301C]" data-testid="pro-badge">
                  <Sun size={10} /> Pro
                </span>
              )}
              <span className="hidden md:inline text-sm font-medium text-[#3A4A3E]" data-testid="user-name">{user.name}</span>
              <button onClick={() => { logout(); nav("/"); }} data-testid="logout-btn" className="p-2 rounded-full hover:bg-[#EAE7E0] transition-colors">
                <LogOut size={16} className="text-[#17301C]" />
              </button>
            </div>
          ) : (
            <>
              <Link to="/login" className="text-sm font-semibold text-[#17301C] hidden lg:inline-block hover:underline" data-testid="nav-login">{t.nav.login}</Link>
              <Link to="/register" className="btn-primary text-xs sm:text-sm py-2 px-3 sm:px-4" data-testid="nav-register">{t.nav.register}</Link>
            </>
          )}

          {/* Mobile hamburger */}
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <button
                aria-label="Apri menu"
                data-testid="mobile-menu-btn"
                className="lg:hidden flex items-center justify-center w-10 h-10 rounded-full border-2 border-[#17301C] bg-white hover:bg-[#EAB308] transition-colors"
              >
                <Menu size={20} className="text-[#17301C]" />
              </button>
            </SheetTrigger>
            <SheetContent side="right" className="w-full sm:max-w-sm bg-[#F9F8F6] border-l-2 border-[#17301C] p-0 flex flex-col">
              <SheetTitle className="sr-only">Menu di navigazione</SheetTitle>
              <SheetDescription className="sr-only">Naviga tra le sezioni di SolarControl Minniti</SheetDescription>

              <div className="flex items-center justify-between px-6 py-5 border-b border-[#DDE0D8]">
                <Link to="/" onClick={closeAnd} className="flex items-center gap-2" data-testid="mobile-logo">
                  <img src="/logo.jpg" alt="SolarControl Minniti" className="h-10 w-10 object-contain rounded-full bg-white" />
                  <div className="flex flex-col leading-none">
                    <span className="text-base font-black">
                      <span className="text-[#EAB308]">Solar</span><span className="text-[#17301C]">Control</span>
                    </span>
                    <span className="text-[10px] font-black tracking-[0.35em] text-[#17301C] uppercase">Minniti</span>
                  </div>
                </Link>
              </div>

              {user && (
                <div className="px-6 py-4 border-b border-[#DDE0D8] bg-white">
                  <div className="flex items-center gap-3">
                    <div className="w-11 h-11 rounded-full bg-[#EAB308] flex items-center justify-center font-black text-[#17301C] text-lg">
                      {(user.name || "?").charAt(0).toUpperCase()}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="font-bold text-[#17301C] truncate" data-testid="mobile-user-name">{user.name}</div>
                      <div className="text-xs text-[#6E7A71] truncate">{user.email}</div>
                    </div>
                    {user.is_pro && (
                      <span className="inline-flex items-center gap-1 text-[10px] font-black tracking-widest uppercase px-2 py-1 rounded-full bg-[#EAB308] text-[#17301C]">
                        <Sun size={10} /> Pro
                      </span>
                    )}
                  </div>
                </div>
              )}

              <nav className="flex-1 overflow-y-auto px-4 py-5 space-y-2">
                {navItems.map((item) => (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    className={mobileLinkCls}
                    data-testid={`mobile-${item.testid}`}
                    onClick={closeAnd}
                  >
                    <span>{item.label}</span>
                    <span className="text-lg">›</span>
                  </NavLink>
                ))}
              </nav>

              <div className="border-t border-[#DDE0D8] p-4 space-y-2 bg-white">
                {user ? (
                  <button
                    onClick={() => { logout(); closeAnd(); nav("/"); }}
                    data-testid="mobile-logout"
                    className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl border-2 border-[#17301C] text-[#17301C] font-bold hover:bg-[#17301C] hover:text-white transition-colors"
                  >
                    <LogOut size={16} /> Esci
                  </button>
                ) : (
                  <>
                    <Link
                      to="/login"
                      onClick={closeAnd}
                      data-testid="mobile-login"
                      className="w-full flex items-center justify-center px-4 py-3 rounded-xl border-2 border-[#17301C] text-[#17301C] font-bold hover:bg-[#17301C] hover:text-white transition-colors"
                    >
                      {t.nav.login}
                    </Link>
                    <Link
                      to="/register"
                      onClick={closeAnd}
                      data-testid="mobile-register"
                      className="w-full flex items-center justify-center px-4 py-3 rounded-xl bg-[#EAB308] text-[#17301C] font-black hover:bg-[#f5bc1b] transition-colors"
                    >
                      {t.nav.register}
                    </Link>
                  </>
                )}
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
};
